<script setup>
// Isletme ve faturalama: odeme bilgisi, hesap harcama limiti ve faturalar.
//
// Harcama limiti para harcamayi dogrudan etkiledigi icin degistirme akisi
// onayli; MCP tarafi da ads_update_spend_cap icin onay bekliyor.
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import { aracCalistir, diziyeCevir, paraBicimle } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: null },
});

const { t } = useI18n();
const route = useRoute();

const hesapKimligi = computed(() => route.params.accountId);
const birim = computed(() => props.hesapAyrinti?.currency || 'TRY');

// Meta bu alanlari kurus olarak dondurur; ham sayi gosterirsek 100 kat yanlis
// bir limit okunur, o yuzden ad bazli ceviriyoruz.
const PARA_ALANLARI = [
  'spend_cap',
  'amount_spent',
  'balance',
  'min_daily_budget',
  'funding_source_amount',
];

// Fatura donemi ve son odeme gunu ISO dizesi ya da Unix saniyesi gelir; ham
// damga ekranda okunmaz oldugu icin yerel bicime ceviriyoruz.
const TARIH_ALANLARI = [
  'next_bill_date',
  'created_time',
  'updated_time',
  'invoice_date',
  'payment_date',
  'due_date',
];

const tarihBicimle = deger => {
  const ham = String(deger);
  // Unix saniyesi 10 hanelidir; cozulemeyen deger oldugu gibi gosterilir ki
  // "2024-01-01 - 2024-01-31" gibi donem dizeleri kaybolmasin.
  const zaman = /^\d{10}$/.test(ham) ? new Date(Number(ham) * 1000) : new Date(ham);
  return Number.isNaN(zaman.getTime())
    ? ham
    : zaman.toLocaleDateString('tr-TR', { dateStyle: 'medium' });
};

// Meta alan adlarini snake_case dondurur. Ham anahtar ("amount_spent")
// kullaniciya hicbir sey anlatmaz; cevirisi olani Turkce basliga ceviriyoruz,
// cevirisi olmayani uydurmadan oldugu gibi birakiyoruz (vue-i18n karsilik
// bulamayinca anahtarin kendisini dondurur).
const ALAN_ONEKI = 'META_ADS.AYARLAR.ISLETME.ALAN.';

const alanEtiketi = anahtar => {
  const yol = ALAN_ONEKI + anahtar.toUpperCase();
  const metin = t(yol);
  return metin === yol ? anahtar : metin;
};

// Ic ice nesne/dizi tek satira sigmaz; ham JSON yerine ozet gosteriyoruz.
const icerikOzeti = deger => {
  if (Array.isArray(deger)) {
    return t('META_ADS.AYARLAR.ORTAK.OGE_SAYISI', { sayi: deger.length });
  }
  const tanitici = deger.display_string || deger.name || deger.id;
  if (tanitici) return String(tanitici);
  return t('META_ADS.AYARLAR.ORTAK.ALAN_SAYISI', { sayi: Object.keys(deger).length });
};

// Meta'da spend_cap = 0 "limit yok" anlamina gelir. Oldugu gibi bicimlersek
// ekranda "0,00 ₺" yazar ve kullanici hesabin hic harcama yapamadigini sanir.
const alanDegeri = (anahtar, deger) => {
  if (deger === null || deger === '') return '—';
  if (anahtar === 'spend_cap' && Number(deger) === 0) {
    return t('META_ADS.AYARLAR.ISLETME.LIMIT_SINIRSIZ');
  }
  if (typeof deger === 'boolean') {
    return deger ? t('META_ADS.AYARLAR.ORTAK.EVET') : t('META_ADS.AYARLAR.ORTAK.HAYIR');
  }
  if (typeof deger === 'object') return icerikOzeti(deger);
  if (PARA_ALANLARI.includes(anahtar)) return paraBicimle(deger, birim.value);
  if (TARIH_ALANLARI.includes(anahtar)) return tarihBicimle(deger);
  return String(deger);
};

const alanlaraCevir = veri =>
  Object.entries(veri || {})
    .filter(([, d]) => d !== undefined)
    .map(([anahtar, deger]) => ({
      anahtar,
      etiket: alanEtiketi(anahtar),
      deger: alanDegeri(anahtar, deger),
    }));

const tekNesne = sonuc => (Array.isArray(sonuc?.veri) ? sonuc.veri[0] : sonuc?.veri);

// --- Faturalama bilgisi ---------------------------------------------------
const faturalamaAlanlari = ref([]);
const faturalamaOzeti = ref('');
const faturalamaYukleniyor = ref(false);
const faturalamaHatasi = ref('');

const faturalamaYukle = async () => {
  if (!props.hesap) return;
  faturalamaYukleniyor.value = true;
  faturalamaHatasi.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'ads_get_billing_info', {
      account_id: props.hesap,
    });
    faturalamaOzeti.value = sonuc.ozet || '';
    faturalamaAlanlari.value = alanlaraCevir(tekNesne(sonuc));
  } catch (e) {
    faturalamaHatasi.value = e.response?.data?.error || e.message;
  } finally {
    faturalamaYukleniyor.value = false;
  }
};

// --- Harcama limiti -------------------------------------------------------
const limitAlanlari = ref([]);
const limitYukleniyor = ref(false);
const limitHatasi = ref('');
const yeniLimit = ref('');
const limitKaydediliyor = ref(false);

const limitYukle = async () => {
  if (!props.hesap) return;
  limitYukleniyor.value = true;
  limitHatasi.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'ads_get_spend_limit', {
      account_id: props.hesap,
    });
    limitAlanlari.value = alanlaraCevir(tekNesne(sonuc));
  } catch (e) {
    limitHatasi.value = e.response?.data?.error || e.message;
  } finally {
    limitYukleniyor.value = false;
  }
};

const limitGuncelle = async () => {
  const tutar = Number(String(yeniLimit.value).replace(',', '.'));
  if (!Number.isFinite(tutar) || tutar < 0) return;
  // Kullanici lira girer, Meta kurus bekler.
  const kurus = Math.round(tutar * 100);
  const soru = t('META_ADS.AYARLAR.ISLETME.LIMIT_ONAYI', {
    tutar: paraBicimle(kurus, birim.value),
  });
  if (!window.confirm(soru)) return;

  limitKaydediliyor.value = true;
  limitHatasi.value = '';
  try {
    await aracCalistir(
      hesapKimligi.value,
      'ads_update_spend_cap',
      { account_id: props.hesap, spend_cap: kurus },
      { onaylandi: true }
    );
    yeniLimit.value = '';
    useAlert(t('META_ADS.AYARLAR.ISLETME.LIMIT_GUNCELLENDI'));
    await limitYukle();
  } catch (e) {
    limitHatasi.value = e.response?.data?.error || e.message;
  } finally {
    limitKaydediliyor.value = false;
  }
};

// --- Faturalar ------------------------------------------------------------
const faturalar = ref([]);
const faturaYukleniyor = ref(false);
const faturaHatasi = ref('');

// Fatura alan adlari surumden surume degistigi icin adaylari sirayla deniyoruz;
// hicbiri yoksa uydurma sifir yerine tire gosteriyoruz.
const ilkDolu = (satir, adaylar) =>
  adaylar.map(a => satir?.[a]).find(d => d !== undefined && d !== null && d !== '');

const faturaSutunlari = computed(() => [
  { anahtar: 'id', baslik: t('META_ADS.AYARLAR.ISLETME.FATURA_NO') },
  {
    anahtar: 'billing_period',
    baslik: t('META_ADS.AYARLAR.ISLETME.DONEM'),
    bicim: (deger, satir) => {
      const ham = ilkDolu(satir, [
        'billing_period',
        'invoice_date',
        'payment_date',
        'date',
      ]);
      return ham === undefined ? '—' : tarihBicimle(ham);
    },
  },
  {
    anahtar: 'amount_due',
    baslik: t('META_ADS.AYARLAR.ISLETME.TUTAR'),
    bicim: (deger, satir) => {
      const ham = ilkDolu(satir, ['amount_due', 'amount', 'total_amount', 'billed_amount']);
      return ham === undefined ? '—' : paraBicimle(ham, satir.currency || birim.value);
    },
  },
  {
    anahtar: 'payment_status',
    baslik: t('META_ADS.AYARLAR.ISLETME.DURUM'),
    bicim: (deger, satir) => ilkDolu(satir, ['payment_status', 'status']) || '—',
  },
]);

const faturalariYukle = async () => {
  if (!props.hesap) return;
  faturaYukleniyor.value = true;
  faturaHatasi.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'ads_get_invoices', {
      account_id: props.hesap,
    });
    faturalar.value = diziyeCevir(sonuc);
  } catch (e) {
    faturaHatasi.value = e.response?.data?.error || e.message;
  } finally {
    faturaYukleniyor.value = false;
  }
};

const hepsiniYukle = () => {
  faturalamaYukle();
  limitYukle();
  faturalariYukle();
};

watch(() => props.hesap, hepsiniYukle);
onMounted(hepsiniYukle);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <p v-if="!hesap" class="text-sm text-n-slate-11">
      {{ t('META_ADS.AYARLAR.ISLETME.HESAP_SEC') }}
    </p>

    <template v-else>
      <!-- Faturalama bilgisi -->
      <section class="border rounded-lg border-n-weak">
        <header class="flex items-center gap-3 px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.AYARLAR.ISLETME.FATURALAMA') }}
          </h2>
          <span class="grow" />
          <Button
            variant="faded"
            size="sm"
            icon="i-lucide-refresh-cw"
            :label="t('META_ADS.AYARLAR.ISLETME.YENILE')"
            @click="faturalamaYukle"
          />
        </header>

        <p v-if="faturalamaHatasi" class="px-4 py-3 text-sm text-n-ruby-11">
          {{ faturalamaHatasi }}
        </p>

        <p v-if="faturalamaOzeti" class="px-4 pt-3 text-sm whitespace-pre-line text-n-slate-11">
          {{ faturalamaOzeti }}
        </p>

        <dl
          v-if="faturalamaAlanlari.length"
          class="grid grid-cols-2 gap-x-6 gap-y-2 px-4 py-4"
        >
          <div
            v-for="alan in faturalamaAlanlari"
            :key="alan.anahtar"
            class="grid grid-cols-[auto_1fr] gap-x-4 text-sm"
          >
            <dt class="text-n-slate-11">{{ alan.etiket }}</dt>
            <dd class="break-words text-n-slate-12">{{ alan.deger }}</dd>
          </div>
        </dl>

        <p
          v-else-if="!faturalamaYukleniyor && !faturalamaHatasi && !faturalamaOzeti"
          class="px-4 py-6 text-sm text-n-slate-11"
        >
          {{ t('META_ADS.AYARLAR.ISLETME.BILGI_YOK') }}
        </p>
      </section>

      <!-- Harcama limiti -->
      <section class="border rounded-lg border-n-weak">
        <header class="flex items-center gap-3 px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.AYARLAR.ISLETME.HARCAMA_LIMITI') }}
          </h2>
          <span class="grow" />
          <Button
            variant="faded"
            size="sm"
            icon="i-lucide-refresh-cw"
            :label="t('META_ADS.AYARLAR.ISLETME.YENILE')"
            @click="limitYukle"
          />
        </header>

        <p v-if="limitHatasi" class="px-4 py-3 text-sm text-n-ruby-11">{{ limitHatasi }}</p>

        <dl v-if="limitAlanlari.length" class="grid grid-cols-2 gap-x-6 gap-y-2 px-4 py-4">
          <div
            v-for="alan in limitAlanlari"
            :key="alan.anahtar"
            class="grid grid-cols-[auto_1fr] gap-x-4 text-sm"
          >
            <dt class="text-n-slate-11">{{ alan.etiket }}</dt>
            <dd class="break-words text-n-slate-12">{{ alan.deger }}</dd>
          </div>
        </dl>

        <div class="flex flex-wrap items-end gap-3 px-4 py-4 border-t border-n-weak">
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('META_ADS.AYARLAR.ISLETME.YENI_LIMIT') }}
            <input
              v-model="yeniLimit"
              type="number"
              min="0"
              step="1"
              class="!mb-0 w-40"
              :placeholder="birim"
            />
          </label>
          <Button
            variant="solid"
            size="sm"
            :label="t('META_ADS.AYARLAR.ISLETME.LIMITI_GUNCELLE')"
            :disabled="limitKaydediliyor || yeniLimit === ''"
            @click="limitGuncelle"
          />
          <p class="text-xs text-n-slate-11">
            {{ t('META_ADS.AYARLAR.ISLETME.LIMIT_ACIKLAMA') }}
          </p>
        </div>
      </section>

      <!-- Faturalar -->
      <section class="border rounded-lg border-n-weak">
        <header class="flex items-center gap-3 px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.AYARLAR.ISLETME.FATURALAR') }}
          </h2>
          <span class="grow" />
          <Button
            variant="faded"
            size="sm"
            icon="i-lucide-refresh-cw"
            :label="t('META_ADS.AYARLAR.ISLETME.YENILE')"
            @click="faturalariYukle"
          />
        </header>

        <p v-if="faturaHatasi" class="px-4 py-3 text-sm text-n-ruby-11">{{ faturaHatasi }}</p>

        <AracTablosu
          :satirlar="faturalar"
          :sutunlar="faturaSutunlari"
          :yukleniyor="faturaYukleniyor"
          :bos-metin="t('META_ADS.AYARLAR.ISLETME.FATURA_YOK')"
        />
      </section>
    </template>
  </div>
</template>
